import { Alert, Checkbox, Empty, Space, Tabs, Tag, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { AppWindow, Database, Menu as MenuIcon, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  type PrivilegeListResponse,
  RolePrivilegeEditor,
} from '../../components/admin/RolePrivilegeEditor.js';
import { PlatformRoleGeneralPanel } from './PlatformRoleGeneralPanel.js';
import type {
  PlatformAppRow,
  PlatformPermissionTabKey,
  PlatformRoleDetail,
  PlatformSitemapNode,
} from './platform-permission-types.js';

export function buildPlatformMenuTree(items: PlatformSitemapNode[]): DataNode[] {
  const children = new Map<string | null, PlatformSitemapNode[]>();
  for (const item of items) {
    const list = children.get(item.parent_id) ?? [];
    list.push(item);
    children.set(item.parent_id, list);
  }

  const build = (item: PlatformSitemapNode): DataNode => ({
    key: item.sitemap_id,
    title: (
      <Space size={6}>
        <span>{item.display_name_zh}</span>
        {item.custom_url && (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {item.custom_url}
          </Typography.Text>
        )}
      </Space>
    ),
    children: (children.get(item.sitemap_id) ?? []).map(build),
  });

  return (children.get(null) ?? []).map(build);
}

export function PlatformPermissionDrawerTabs(props: {
  activeTab: PlatformPermissionTabKey;
  detail: PlatformRoleDetail;
  apps: PlatformAppRow[];
  selectedAppIds: string[];
  adminTree: DataNode[];
  checkedAdminIds: string[];
  privilegeData: PrivilegeListResponse | null;
  privilegeLoading: boolean;
  privilegeChanges: Map<string, number>;
  privilegeQ: string;
  privilegePage: number;
  privilegePageSize: number;
  onTabChange: (key: PlatformPermissionTabKey) => void;
  onSelectedAppIdsChange: (ids: string[]) => void;
  onCheckedAdminIdsChange: (ids: string[]) => void;
  onPrivilegeChanges: (changes: Map<string, number>) => void;
  onPrivilegeQChange: (q: string) => void;
  onPrivilegePageChange: (page: number, pageSize: number) => void;
  onMembersChanged: () => Promise<void> | void;
}) {
  const { t } = useTranslation('platformPermissions');
  return (
    <Tabs
      activeKey={props.activeTab}
      onChange={(key) => props.onTabChange(key as PlatformPermissionTabKey)}
      items={[
        {
          key: 'general',
          label: (
            <Space size={6}>
              <User size={14} /> {t('general')}
            </Space>
          ),
          children: (
            <PlatformRoleGeneralPanel detail={props.detail} onChanged={props.onMembersChanged} />
          ),
        },
        {
          key: 'apps',
          label: (
            <Space size={6}>
              <AppWindow size={14} /> {t('appAccess')}
              <Tag color="blue">{props.selectedAppIds.length}</Tag>
            </Space>
          ),
          children: (
            <AppAccessTab
              apps={props.apps}
              selectedAppIds={props.selectedAppIds}
              onChange={props.onSelectedAppIdsChange}
            />
          ),
        },
        {
          key: 'menu',
          label: (
            <Space size={6}>
              <MenuIcon size={14} /> {t('menuPermissions')}
              <Tag color="blue">{props.checkedAdminIds.length}</Tag>
            </Space>
          ),
          children: (
            <MenuPermissionTab
              treeData={props.adminTree}
              checkedKeys={props.checkedAdminIds}
              onChange={props.onCheckedAdminIdsChange}
            />
          ),
        },
        {
          key: 'privileges',
          label: (
            <Space size={6}>
              <Database size={14} /> {t('platformDataPrivileges')}
              {props.privilegeChanges.size > 0 && (
                <Tag color="orange">
                  {t('changedCount', { count: props.privilegeChanges.size })}
                </Tag>
              )}
            </Space>
          ),
          children: (
            <RolePrivilegeEditor
              data={props.privilegeData}
              loading={props.privilegeLoading}
              changes={props.privilegeChanges}
              onChange={props.onPrivilegeChanges}
              disabled={false}
              q={props.privilegeQ}
              onQChange={props.onPrivilegeQChange}
              page={props.privilegePage}
              pageSize={props.privilegePageSize}
              onPageChange={props.onPrivilegePageChange}
              boundaryMessage={t('platformDataBoundaryTitle')}
              boundaryDescription={t('platformDataBoundaryDesc')}
            />
          ),
        },
      ]}
    />
  );
}

function AppAccessTab({
  apps,
  selectedAppIds,
  onChange,
}: {
  apps: PlatformAppRow[];
  selectedAppIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation('platformPermissions');
  if (apps.length === 0) return <Empty description={t('noApps')} />;
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Alert type="info" showIcon message={t('assignableAppsDescription')} />
      <Checkbox.Group value={selectedAppIds} onChange={(values) => onChange(values.map(String))}>
        <Space wrap>
          {apps.map((app) => (
            <Checkbox key={app.appid} value={app.appid}>
              {app.display_name_zh}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
    </Space>
  );
}

function MenuPermissionTab({
  treeData,
  checkedKeys,
  onChange,
}: {
  treeData: DataNode[];
  checkedKeys: string[];
  onChange: (keys: string[]) => void;
}) {
  const { t } = useTranslation('platformPermissions');
  return (
    <div>
      <Typography.Paragraph type="secondary">{t('platformMenusDesc')}</Typography.Paragraph>
      {treeData.length === 0 ? (
        <Empty description={t('noMenus')} />
      ) : (
        <Tree
          checkable
          checkedKeys={checkedKeys}
          onCheck={(keys) => onChange((Array.isArray(keys) ? keys : keys.checked).map(String))}
          treeData={treeData}
          defaultExpandAll
          blockNode
        />
      )}
    </div>
  );
}
